import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, CloudDownload, KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Checkbox } from '../../ui/checkbox';
import { haccpApi, erroreHaccp } from '../haccpApi';
import { ElencoHaccpOk } from './ElencoHaccpOk';

const CAMPO = 'mt-1 w-full h-11 px-3 rounded-lg border border-gray-300 text-sm text-gray-900 bg-white';
const CHIAVE_EMAIL = 'haccpok_email';
const BOTTONE = 'w-full h-12 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors';

const Esito = ({ esito }) => (
  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 space-y-1.5" data-testid="haccpok-esito">
    <p className="flex items-center gap-2 font-semibold text-emerald-900"><CheckCircle2 className="h-5 w-5" />{esito.importati} documenti copiati</p>
    {esito.aggiornati > 0 && <p className="text-xs text-emerald-800" data-testid="haccpok-esito-aggiornati">{esito.aggiornati} sono versioni nuove: hanno sostituito quelle vecchie.</p>}
    {esito.gia_presenti > 0 && <p className="text-xs text-emerald-800">{esito.gia_presenti} erano già nell'app.</p>}
    {esito.persone_nuove.length > 0 && <p className="text-xs text-emerald-800" data-testid="haccpok-esito-persone">Persone aggiunte: {esito.persone_nuove.join(', ')}. Chi non lavora più qui lo togli da Persone.</p>}
    {esito.errori.length > 0 && <p className="text-xs text-red-700" data-testid="haccpok-esito-errori">Non copiati: {esito.errori.join('; ')}. Riprova più tardi.</p>}
  </div>
);

const Salvate = ({ email, busy, onVedi, onCambia, onDimentica }) => (
  <div className="space-y-3" data-testid="haccpok-salvate">
    <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 flex items-start gap-2">
      <KeyRound className="h-4 w-4 text-sky-700 shrink-0 mt-0.5" />
      <div className="text-sm text-sky-900">Credenziali salvate per <b data-testid="haccpok-salvate-email">{email}</b>
        <p className="text-[11px] text-sky-800">Protette e usate solo dal responsabile.</p></div>
    </div>
    <button onClick={onVedi} disabled={busy} data-testid="haccpok-vedi-btn" className={BOTTONE}>
      {busy && <Loader2 className="h-4 w-4 animate-spin" />}Vedi i documenti
    </button>
    <div className="flex justify-center gap-4 text-xs">
      <button onClick={onCambia} disabled={busy} className="text-sky-700 font-medium" data-testid="haccpok-cambia-btn">Cambia credenziali</button>
      <button onClick={onDimentica} disabled={busy} className="text-red-600 font-medium" data-testid="haccpok-dimentica-btn">Non ricordarle più</button>
    </div>
  </div>
);

export const ImportaHaccpOk = ({ open, onClose, onImportato }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [ricorda, setRicorda] = useState(false);
  const [salvate, setSalvate] = useState(null);
  const [lista, setLista] = useState(null);
  const [esito, setEsito] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setEmail(localStorage.getItem(CHIAVE_EMAIL) || ''); setPassword(''); setRicorda(false); setLista(null); setEsito(null); setSalvate(null);
    haccpApi.get('/documenti/haccpok/accesso').then((r) => { if (r.salvate) { setSalvate(r.email); setRicorda(true); } }).catch(() => {});
  }, [open]);

  const corpo = () => (salvate && !password ? {} : { email: email.trim(), password, ricorda });
  const chiama = async (path, fai) => {
    setBusy(true);
    try { fai(await haccpApi.post(path, corpo())); }
    catch (e) {
      toast.error(erroreHaccp(e, 'HaccpOK non raggiungibile'));
      if (salvate && !password && e?.response?.status === 400) { setEmail(salvate); setSalvate(null); }
    }
    finally { setBusy(false); }
  };
  const vedi = () => chiama('/documenti/haccpok/anteprima', (r) => {
    if (password) { localStorage.setItem(CHIAVE_EMAIL, email.trim()); setSalvate(ricorda ? email.trim() : null); }
    setLista(r);
  });
  const importa = () => chiama('/documenti/haccpok/importa', (r) => { setEsito(r); setPassword(''); onImportato(); });
  const dimentica = async () => {
    try { await haccpApi.del('/documenti/haccpok/accesso'); toast.success('Credenziali cancellate'); setEmail(salvate); setSalvate(null); setRicorda(false); }
    catch (e) { toast.error(erroreHaccp(e, 'Non cancellate')); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg w-[96vw] max-h-[92vh] overflow-y-auto rounded-2xl" data-testid="haccpok-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6"><CloudDownload className="h-5 w-5 text-sky-700" />Importa da HaccpOK</DialogTitle>
          <DialogDescription>Copio i documenti e gli attestati che il consulente ha caricato su HaccpOK, con le loro scadenze.</DialogDescription>
        </DialogHeader>
        {!lista && salvate && <Salvate email={salvate} busy={busy} onVedi={vedi} onDimentica={dimentica} onCambia={() => { setEmail(salvate); setSalvate(null); }} />}
        {!lista && !salvate && (
          <div className="space-y-3">
            <label className="block text-xs text-gray-600">Email di HaccpOK
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={CAMPO} autoComplete="off" data-testid="haccpok-email" />
            </label>
            <label className="block text-xs text-gray-600">Password di HaccpOK
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={CAMPO} autoComplete="off"
                onKeyDown={(e) => e.key === 'Enter' && email && password && vedi()} data-testid="haccpok-password" />
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer select-none">
              <Checkbox checked={ricorda} onCheckedChange={(v) => setRicorda(!!v)} data-testid="haccpok-ricorda" />Ricorda le credenziali
            </label>
            <p className="flex items-start gap-1.5 text-[11px] text-gray-500" data-testid="haccpok-nota-password"><ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-px" />
              {ricorda ? 'Email e password restano salvate protette per il ristorante: le usa solo il responsabile e puoi cancellarle quando vuoi.'
                : 'La password serve solo per questa lettura: non viene salvata.'}</p>
            <button onClick={vedi} disabled={busy || !email.trim() || !password} data-testid="haccpok-vedi-btn" className={BOTTONE}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}Vedi i documenti
            </button>
          </div>
        )}
        {lista && !esito && (
          <div className="space-y-3">
            <p className="text-sm text-gray-700" data-testid="haccpok-conteggio">{lista.documenti.length} documenti su HaccpOK, <b>{lista.nuovi} da copiare</b>.</p>
            {lista.documenti.some((d) => d.nuova_versione && !d.superato) && <p className="text-[11px] text-gray-500" data-testid="haccpok-nota-versioni">"Nuova versione": il consulente ha caricato un file nuovo al posto del vecchio, che viene sostituito.</p>}
            {lista.documenti.some((d) => d.storico && !d.gia_importato) && <p className="text-[11px] text-gray-500">"Storico": c'è un documento più recente dello stesso tipo, lo conservo come versione precedente.</p>}
            {lista.documenti.some((d) => d.superato && !d.gia_importato) && <p className="text-[11px] text-gray-500" data-testid="haccpok-nota-superati">"Superato": c'è una versione più recente, il vecchio non si copia. Se nell'app c'è un documento più vecchio, viene eliminato e sostituito.</p>}
            <ElencoHaccpOk documenti={lista.documenti} />
            <button onClick={importa} disabled={busy || !lista.nuovi} data-testid="haccpok-importa-btn"
              className="w-full h-12 rounded-full bg-gray-900 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}{busy ? 'Copio i documenti...' : lista.nuovi ? `Copia ${lista.nuovi} documenti` : 'Tutto già copiato'}
            </button>
          </div>
        )}
        {esito && <Esito esito={esito} />}
        {esito && <button onClick={onClose} data-testid="haccpok-chiudi-btn" className="w-full h-11 rounded-full border border-gray-300 bg-white text-sm font-semibold text-gray-800">Chiudi</button>}
      </DialogContent>
    </Dialog>
  );
};
